import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { t, font, Button, Badge } from "@realtyflows/ui";
import { getContractTemplates, getContractFolder, updateContractFolder, addFolderForm, deleteFolderForm, reorderFolderForms, sendEnvelope, getDeals } from "../api";
import SearchSelect from "../components/SearchSelect";
import useResponsive from "../hooks/useResponsive";

const FORM_CATEGORIES = [
  { key: "all", label: "All" },
  { key: "Representation", label: "Representation" },
  { key: "Listing", label: "Listing" },
  { key: "Purchase", label: "Purchase" },
  { key: "Negotiation", label: "Negotiation" },
  { key: "Lease", label: "Lease" },
];

export default function FolderDetail({ user }) {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const responsive = useResponsive();

  const [folder, setFolder] = useState(null);
  const [folderForms, setFolderForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allForms, setAllForms] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formCategory, setFormCategory] = useState("all");
  const [editingName, setEditingName] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [toast, setToast] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({ deal_id: "", signer1Name: "", signer1Email: "", signer2Name: "", signer2Email: "" });
  const [sending, setSending] = useState(false);
  const [deals, setDeals] = useState([]);

  const fetchFolder = async () => {
    try {
      const data = await getContractFolder(folderId);
      setFolder(data);
      setFolderForms(data.forms || []);
      setFolderName(data.name);
    } catch { navigate("/contracts"); }
    setLoading(false);
  };

  useEffect(() => {
    fetchFolder();
    getContractTemplates().then(setAllForms).catch(() => {});
  }, [folderId]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const showToast = (msg, variant = "success") => setToast({ msg, variant });

  const handleRename = async () => {
    if (!folderName.trim()) return;
    try {
      await updateContractFolder(folderId, { name: folderName.trim() });
      setFolder(prev => ({ ...prev, name: folderName.trim() }));
      setEditingName(false);
      showToast("Folder renamed");
    } catch (err) { showToast(err.message, "error"); }
  };

  const handleAddForm = async (form) => {
    try {
      const added = await addFolderForm(folderId, form.id);
      setFolderForms(prev => [...prev, added]);
      setShowAddForm(false);
      showToast(`${form.name} added`);
    } catch (err) { showToast(err.message, "error"); }
  };

  const handleRemoveForm = async (formId) => {
    try {
      await deleteFolderForm(folderId, formId);
      setFolderForms(prev => prev.filter(f => f.id !== formId));
      showToast("Form removed");
    } catch (err) { showToast(err.message, "error"); }
  };

  const handleMoveForm = async (fromIdx, toIdx) => {
    const forms = [...folderForms];
    const [moved] = forms.splice(fromIdx, 1);
    forms.splice(toIdx, 0, moved);
    setFolderForms(forms);
    try {
      await reorderFolderForms(folderId, forms.map(f => f.id));
    } catch {}
  };

  const openSendModal = async () => {
    const readyForms = folderForms.filter(f => f.status === "ready" || f.status === "filled");
    if (readyForms.length === 0) {
      showToast("Fill and prepare at least one form before sending", "error");
      return;
    }
    try { const d = await getDeals(); setDeals(d); } catch {}
    setShowSendModal(true);
  };

  const handleSendEnvelope = async () => {
    if (!sendForm.signer1Email) { showToast("Signer 1 email is required", "error"); return; }
    setSending(true);
    try {
      const readyForms = folderForms.filter(f => f.status === "ready" || f.status === "filled");
      const formsToSend = readyForms.map(form => ({
        template_id: form.template_id,
        filled_data: form.filled_data || {},
      }));

      const signers = [{ name: sendForm.signer1Name, email: sendForm.signer1Email, role: "Signer 1", order: 1 }];
      if (sendForm.signer2Email) {
        signers.push({ name: sendForm.signer2Name, email: sendForm.signer2Email, role: "Signer 2", order: 2 });
      }

      const result = await sendEnvelope({
        forms: formsToSend,
        signers,
        folder_name: folder.name,
        deal_id: sendForm.deal_id || undefined,
      });

      await updateContractFolder(folderId, { status: "sent" });
      setFolder(prev => ({ ...prev, status: "sent" }));
      showToast(`${result.documents_sent} document(s) sent for signing!`);
      setShowSendModal(false);
    } catch (err) {
      showToast(err.message || "Failed to send", "error");
    }
    setSending(false);
  };

  const filteredForms = useMemo(() => {
    if (formCategory === "all") return allForms;
    return allForms.filter(f => f.category === formCategory);
  }, [allForms, formCategory]);

  const folderFormIds = useMemo(() => new Set(folderForms.map(f => f.template_id)), [folderForms]);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: t.textMuted }}>Loading...</div>;
  if (!folder) return null;

  const readyCount = folderForms.filter(f => f.status === "filled" || f.status === "ready").length;

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={() => navigate("/contracts")} style={{ background: "none", border: "none", color: t.textSecondary, fontSize: 13, cursor: "pointer", fontFamily: font.body, fontWeight: 600 }}>
          &larr; Back to Folders
        </button>
        <span style={{ color: t.border }}>|</span>
        {editingName ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input value={folderName} onChange={e => setFolderName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleRename(); if (e.key === "Escape") setEditingName(false); }} autoFocus
              style={{ fontSize: 18, fontWeight: 700, color: t.textPrimary, fontFamily: font.display, border: `1px solid ${t.border}`, borderRadius: 6, padding: "4px 10px", outline: "none", width: 300 }} />
            <Button variant="primary" size="sm" onClick={handleRename}>Save</Button>
            <Button variant="outline" size="sm" onClick={() => setEditingName(false)}>Cancel</Button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>{folder.name}</div>
            <button onClick={() => setEditingName(true)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted }} title="Rename">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
            </button>
          </div>
        )}
      </div>

      {/* Stats Bar */}
      <div style={{ display: "flex", gap: 16, marginBottom: 20, padding: "12px 16px", background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, color: t.textSecondary, fontFamily: font.body }}><strong style={{ color: t.textPrimary }}>{folderForms.length}</strong> form(s)</div>
        <div style={{ width: 1, height: 20, background: t.border }} />
        <div style={{ fontSize: 12, color: t.textSecondary, fontFamily: font.body }}><strong style={{ color: t.emerald }}>{readyCount}</strong> ready</div>
        <div style={{ width: 1, height: 20, background: t.border }} />
        <Badge variant={folder.status === "sent" ? "sky" : folder.status === "signed" ? "emerald" : "default"}>
          {folder.status === "sent" ? "Sent" : folder.status === "signed" ? "Signed" : "Draft"}
        </Badge>
        <div style={{ flex: 1 }} />
        <Button variant="primary" size="sm" onClick={() => setShowAddForm(true)}>+ Add Form</Button>
        {folderForms.length > 0 && (
          <Button variant={folder.status === "sent" ? "outline" : "primary"} size="sm" onClick={openSendModal} disabled={folder.status === "sent"}
            style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            {folder.status === "sent" ? "Envelope Sent" : "Send Envelope"}
          </Button>
        )}
      </div>

      {/* Forms List */}
      {folderForms.length === 0 ? (
        <div style={{ padding: 50, textAlign: "center", background: t.surface, border: `1.5px dashed ${t.border}`, borderRadius: 12 }}>
          <div style={{ fontSize: 40, opacity: 0.15, marginBottom: 12 }}>{"\uD83D\uDCC4"}</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: t.textSecondary, fontFamily: font.body, marginBottom: 8 }}>No forms in this folder yet</div>
          <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, marginBottom: 20, lineHeight: 1.6 }}>Add forms from the library, fill them out, then send all at once.</div>
          <Button variant="primary" onClick={() => setShowAddForm(true)}>+ Add Form</Button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {folderForms.map((form, idx) => (
            <div key={form.id} style={{
              background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8,
              padding: "12px 16px", display: "flex", alignItems: "center", gap: 12,
              borderLeft: `3px solid ${form.status === "ready" ? t.emerald : form.status === "filled" ? t.sky : form.status === "signed" ? t.emerald : t.border}`,
            }}>
              {/* Order arrows */}
              <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
                {idx > 0 && <button onClick={() => handleMoveForm(idx, idx - 1)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, padding: 0, lineHeight: 1 }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15"/></svg></button>}
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, fontFamily: "monospace", textAlign: "center", width: 16 }}>{idx + 1}</div>
                {idx < folderForms.length - 1 && <button onClick={() => handleMoveForm(idx, idx + 1)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, padding: 0, lineHeight: 1 }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg></button>}
              </div>

              {/* Icon */}
              <div style={{ width: 34, height: 34, borderRadius: 8, background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              </div>

              {/* Name */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{form.template_name || form.name}</div>
                <div style={{ fontSize: 10, color: t.textMuted, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: 0.5 }}>{form.category}</div>
              </div>

              {/* Status + tags */}
              <Badge variant={form.status === "ready" ? "emerald" : form.status === "filled" ? "sky" : "default"}>
                {form.status === "ready" ? "Ready" : form.status === "filled" ? "Filled" : form.status === "signed" ? "Signed" : "Draft"}
              </Badge>
              {form.esign_labels?.length > 0 && <div style={{ fontSize: 9, color: t.emerald, fontWeight: 700, fontFamily: "monospace" }}>{form.esign_labels.length} tags</div>}

              {/* Actions */}
              <div style={{ display: "flex", gap: 6 }}>
                <Button variant="primary" size="sm" onClick={() => navigate(`/contracts/templates/${form.template_id}`)}>
                  {form.status === "draft" ? "Fill" : "Edit"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate(`/contracts/folders/${folderId}/prepare/${form.id}?templateId=${form.template_id}`)}
                  style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                  E-Sign
                </Button>
                <button onClick={() => handleRemoveForm(form.id)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, padding: 4 }} title="Remove">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Form Modal */}
      {showAddForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: t.surface, borderRadius: 16, padding: 28, maxWidth: 560, width: "100%", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>Add Form to Folder</div>
              <button onClick={() => setShowAddForm(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: t.textMuted }}>&times;</button>
            </div>
            <div style={{ display: "flex", gap: 4, marginBottom: 14, flexWrap: "wrap" }}>
              {FORM_CATEGORIES.map(cat => (
                <button key={cat.key} onClick={() => setFormCategory(cat.key)} style={{
                  padding: "5px 12px", fontSize: 10, fontWeight: formCategory === cat.key ? 700 : 500,
                  color: formCategory === cat.key ? t.emerald : t.textSecondary,
                  background: formCategory === cat.key ? `${t.emerald}12` : "transparent",
                  border: `1px solid ${formCategory === cat.key ? `${t.emerald}40` : "transparent"}`,
                  borderRadius: 6, cursor: "pointer", fontFamily: font.body,
                }}>{cat.label}</button>
              ))}
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {filteredForms.map(form => {
                const alreadyAdded = folderFormIds.has(form.id);
                return (
                  <div key={form.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderBottom: `1px solid ${t.border}`, opacity: alreadyAdded ? 0.5 : 1 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{form.name}</div>
                      <div style={{ fontSize: 10, color: t.textMuted, fontFamily: "monospace", textTransform: "uppercase" }}>{form.category}</div>
                    </div>
                    <Button variant={alreadyAdded ? "outline" : "primary"} size="sm" disabled={alreadyAdded} onClick={() => handleAddForm(form)}>
                      {alreadyAdded ? "Added" : "+ Add"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Send Envelope Modal */}
      {showSendModal && (() => {
        const readyForms = folderForms.filter(f => f.status === "ready" || f.status === "filled");
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div style={{ background: t.surface, borderRadius: 16, padding: 28, maxWidth: 480, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.textPrimary, marginBottom: 6, fontFamily: font.display }}>Send Envelope</div>
              <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, marginBottom: 16 }}>{readyForms.length} document(s) will be sent as one signing request</div>

              <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 12, marginBottom: 16, maxHeight: 120, overflowY: "auto" }}>
                {readyForms.map((form, i) => (
                  <div key={form.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12, fontFamily: font.body, color: t.textPrimary }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, fontFamily: "monospace", width: 18 }}>{i + 1}.</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={t.emerald} strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    {form.template_name || form.name}
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: t.textSecondary, fontFamily: font.body, display: "block", marginBottom: 4 }}>Link to Deal (optional)</label>
                <SearchSelect options={deals.map(d => ({ value: d.id, label: `${d.client_name || "Deal"} — ${d.property_address || d.status}` }))} value={sendForm.deal_id} onChange={v => setSendForm({ ...sendForm, deal_id: v })} placeholder="Search deals..." />
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: "#16a34a", marginBottom: 6, fontFamily: "monospace", display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#16a34a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>1</div>
                SIGNER 1
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                <input value={sendForm.signer1Name} onChange={e => setSendForm({ ...sendForm, signer1Name: e.target.value })} placeholder="Full Name" style={{ padding: "9px 12px", borderRadius: 8, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, outline: "none" }} />
                <input value={sendForm.signer1Email} onChange={e => setSendForm({ ...sendForm, signer1Email: e.target.value })} placeholder="Email *" style={{ padding: "9px 12px", borderRadius: 8, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, outline: "none" }} />
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", marginBottom: 6, fontFamily: "monospace", display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#2563eb", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>2</div>
                SIGNER 2 (optional)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
                <input value={sendForm.signer2Name} onChange={e => setSendForm({ ...sendForm, signer2Name: e.target.value })} placeholder="Full Name" style={{ padding: "9px 12px", borderRadius: 8, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, outline: "none" }} />
                <input value={sendForm.signer2Email} onChange={e => setSendForm({ ...sendForm, signer2Email: e.target.value })} placeholder="Email" style={{ padding: "9px 12px", borderRadius: 8, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, outline: "none" }} />
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <Button variant="outline" onClick={() => setShowSendModal(false)} style={{ flex: 1 }}>Cancel</Button>
                <Button variant="primary" onClick={handleSendEnvelope} disabled={sending} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  {sending ? "Sending..." : `Send ${readyForms.length} Document(s)`}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: toast.variant === "error" ? "#fee2e2" : "#dcfce7",
          color: toast.variant === "error" ? "#dc2626" : "#16a34a",
          padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600,
          fontFamily: font.body, boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 9999,
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
